import type { UseQueryResult } from "@tanstack/react-query";

import { isUnauthorizedError } from "@/lib/api-types";

import { EmptyState } from "@/components/states/empty-state";
import { ErrorState } from "@/components/states/error-state";
import { Spinner } from "@/components/states/spinner";
import { UnauthorizedState } from "@/components/states/unauthorized-state";

export interface QueryBoundaryProps<T> {
  query: UseQueryResult<T>;
  /** Render the resolved data. */
  children: (data: T) => React.ReactNode;
  /** Custom loading fallback (e.g. a skeleton matching the surface). */
  loadingFallback?: React.ReactNode;
  /** Treat resolved data as "empty" (e.g. `(d) => d.items.length === 0`). */
  isEmpty?: (data: T) => boolean;
  /** Custom empty fallback. */
  emptyFallback?: React.ReactNode;
  errorTitle?: string;
  /** Custom error fallback. Receives a `retry` that refetches the query. */
  errorFallback?: (retry: () => void) => React.ReactNode;
}

/**
 * One place that turns a TanStack Query result into loading / unauthorized /
 * error / empty / data UI (ENGINEERING.md §5, DECISIONS ADR-0007). Async
 * surfaces wrap their query here instead of branching on `isLoading`/`isError`
 * by hand. Errors are already reported via the QueryCache `onError`
 * (query-client.ts), so this only renders — it doesn't re-report.
 */
export function QueryBoundary<T>({
  query,
  children,
  loadingFallback,
  isEmpty,
  emptyFallback,
  errorTitle,
  errorFallback,
}: QueryBoundaryProps<T>) {
  if (query.isPending) {
    return (
      <>
        {loadingFallback ?? (
          <div className="flex min-h-40 items-center justify-center">
            <Spinner />
          </div>
        )}
      </>
    );
  }

  if (query.isError) {
    // A 401 is an auth state, not a failure: retrying can't fix it and
    // "Something went wrong" misdescribes it. This wins over `errorFallback`
    // because no per-surface error UI can offer the one action that helps.
    if (isUnauthorizedError(query.error)) return <UnauthorizedState />;

    const retry = () => {
      void query.refetch();
    };
    if (errorFallback) return <>{errorFallback(retry)}</>;
    return <ErrorState title={errorTitle} onRetry={retry} />;
  }

  // Past the pending/error guards the query has resolved, so data is present.
  const data = query.data as T;

  if (isEmpty?.(data)) {
    return <>{emptyFallback ?? <EmptyState title="Nothing here yet" />}</>;
  }

  return <>{children(data)}</>;
}
